/**
 * FirmaDetayPage.jsx — bir firmanın tek ekrandaki tam görünümü (denetmen/admin tarafı)
 * Route: /Management/Firmalar/Detay/:firmaId?fuarId=
 *
 * Servis: services/firmaFuarKartService.js → GET /firma/{firmaId}/detay
 * Rehber: docs/guides/FE-FirmaController-guide.md §1
 *
 * Firma Kartları sayfasından (FirmaKartlarPage.jsx) bir karta tıklayınca buraya gelinir.
 * Üstte firmanın kimlik başlığı (Velzon `profile-wid` deseni: avatar + ad + rol
 * rozetleri + özet sayaçlar), altında 2026-09-12'ye kadar ÜÇ AYRI MENÜ GİRDİSİ olan
 * ekranlar sekme olarak:
 *
 *   Profil & Belgeler   → FirmaProfilIncelemePage  (firma belgesi onay/red)
 *   Personel Listesi    → PersonelIncelemePage     (fuar kadrosu + daimî liste)
 *   Taşeron Bağlantıları→ TaseronBaglantilariPage  (bu firmaya filtreli)
 *   Kurulum Saati       → FirmaKurulumSaatiSekmesi (#718: firmaya özel kurulum penceresi, fuar seçicili; İSG Taahhütnamesi bunu basar)
 *   Portal Kullanıcıları→ FirmaPortalKullanicilariSekmesi (#417: girişler ve portal rolleri; atama portal.kullanici_rol.manage ister)
 *   Portal Kısıtları    → FirmaPortalKisitlariSekmesi     (#417: SAKA'nın firma başına modül kısıtı; ekleme / kaldırma aynı izin)
 *
 * Üç sayfa da `embedded` bayrağıyla kendi sayfa kabuğunu (page-content/Container/
 * BreadCrumb/firmaId-fuarId mini formu) kapatıp sadece içeriğini basar — bkz.
 * Components/Common/EmbeddablePage.jsx. Taşeron ekranı ayrıca kendi bağımsız
 * route'unda (fuar geneli kuyruk) durmaya devam ediyor.
 *
 * Sekme seçimi `?tab=` ile URL'de tutuluyor: bir sekmeyi paylaşmak/yenilemek çalışıyor.
 */
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Container, Row, Col, Card, CardBody, Button, Badge, Spinner, Alert,
  Nav, NavItem, NavLink, TabContent, TabPane,
} from "reactstrap";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import { initials, StatBox } from "../../../../Components/Common/DetailHeaderBits";
import MainContext from "../../../../Components/Contexts/MainContext";
import { getFirmaDetay } from "../../../../services/firmaFuarKartService";
// #739: the preview opens in a new tab. WAS: import { startOnizleme } from "../../../../services/portalOnizleme"; (startOnizleme + navigate("/Firma/Anasayfa") in the same tab)
import { openOnizlemeYeniSekme } from "../../../../services/portalOnizleme";
// #614: the pending user addition requests of this company (badge on Portal Kullanıcıları), for those who may decide them.
import { useYetki, PERMISSION_KEYS } from "../../../../services/rbacService";
import { getBekleyenKullaniciTalepSayisi } from "../../../../services/kullaniciTalebiService";
import { DEFAULT_FUAR_ID } from "../../../../services/personnelAdminService";
import { PERSONEL_LISTE_ANAHTARLARI } from "../../../../Components/Kisi/personelListeDurumu";
import FirmaProfilIncelemePage from "./FirmaProfilIncelemePage";
import PersonelIncelemePage from "./PersonelIncelemePage";
import TaseronBaglantilariPage from "./TaseronBaglantilariPage";
import FirmaPortalKullanicilariSekmesi from "./FirmaPortalKullanicilariSekmesi";
import FirmaPortalKisitlariSekmesi from "./FirmaPortalKisitlariSekmesi";
import FirmaKurulumSaatiSekmesi from "./FirmaKurulumSaatiSekmesi";
import { FuarKatalogBilgileriCard } from "../../../../Components/Firma/FirmaBilgiKartlari";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n";

const TABS = [
  { key: "profil",  get label() { return i18n.t("moduller:firmaDetay.profilBelgeler"); },    icon: "bx-building-house" },
  { key: "personel", get label() { return i18n.t("moduller:firmaDetay.personelListesi"); },    icon: "bx-id-card" },
  { key: "taseron", get label() { return i18n.t("moduller:firmaDetay.taseronBaglantilari"); }, icon: "bx-link" },
  // Backlog #104 — FE half of #103: per-fuar catalog, separate from the global one on Profil & Belgeler.
  { key: "fuar-katalog", get label() { return i18n.t("moduller:firmaDetay.fuarKatalogBilgileri"); }, icon: "bx-store" },
  // #718 (backend #717): the company's own build-up window at a fair, the one its İSG Taahhütnamesi prints.
  { key: "kurulum-saati", get label() { return i18n.t("moduller:firmaDetay.kurulumSaati"); }, icon: "bx-time-five" },
  // #417 (design #409 D4 / D9): the company's logins with their portal roles, and SAKA's restrictions on its portal.
  { key: "portal-kullanicilar", get label() { return i18n.t("moduller:firmaDetay.portalKullanicilari"); }, icon: "bx-user-check" },
  { key: "portal-kisitlar", get label() { return i18n.t("moduller:firmaDetay.portalKisitlari"); }, icon: "bx-block" },
];

/** Rol adına göre rozet rengi — Firma Kartları'ndaki FIRMA_ROLE_META ile aynı palet. */
const ROLE_COLOR_BY_ID = { 1: "primary", 3: "info", 2: "warning" };

export default function FirmaDetayPage() {
  const { t: tx } = useTranslation("moduller");
  const { firmaId: firmaIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedFuarId } = useContext(MainContext);

  const firmaId = Number(firmaIdParam);
  const fuarId = Number(searchParams.get("fuarId")) || Number(selectedFuarId) || DEFAULT_FUAR_ID;

  const tabParam = searchParams.get("tab");
  const activeTab = TABS.some((t) => t.key === tabParam) ? tabParam : "profil";

  const [detay, setDetay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // #614: only kullanici.olustur holders can read the count (the backend refuses the others), so nobody else asks.
  const talepKarari = useYetki(PERMISSION_KEYS.KULLANICI_OLUSTUR) === true;
  const [bekleyenTalep, setBekleyenTalep] = useState(0);

  useEffect(() => { document.title = i18n.t("moduller:firmaDetay.firmaDetayFds"); }, []);

  useEffect(() => {
    if (!talepKarari || !Number.isFinite(firmaId)) return undefined;
    let ignore = false;
    async function run() {
      try {
        const n = await getBekleyenKullaniciTalepSayisi(firmaId);
        if (!ignore) setBekleyenTalep(n);
      } catch {
        if (!ignore) setBekleyenTalep(0);   // a badge that cannot be read is simply not drawn
      }
    }
    run();
    return () => { ignore = true; };
  }, [talepKarari, firmaId]);

  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const data = await getFirmaDetay(firmaId, fuarId);
        if (!ignore) setDetay(data);
      } catch (e) {
        if (!ignore) setError(e?.response?.data?.message || e.message || i18n.t("moduller:firmaDetay.firmaOkunamadi"));
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    if (Number.isFinite(firmaId)) run();
    return () => { ignore = true; };
  }, [firmaId, fuarId]);

  const setTab = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    // #728: Personel Listesi'nin adreste tutulan durumu (sekme · arama · sayfa · boyut) başka bir sekmeye geçince düşer.
    PERSONEL_LISTE_ANAHTARLARI.forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
  };

  const profile = detay?.profile;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("firmaDetay.firmaDetay")} pageTitle={tx("firmaDetay.firmalar")} />

        {error && <Alert color="danger">{error}</Alert>}

        {/* ─── Kimlik başlığı ──────────────────────────────────────────────── */}
        <Card className="mb-0">
          <CardBody className="pb-2">
            {loading ? (
              <div className="text-center py-3"><Spinner color="primary" size="sm" /></div>
            ) : (
              <Row className="g-3 align-items-center">
                <Col xs="auto">
                  <div className="avatar-md">
                    <span className="avatar-title rounded bg-primary-subtle text-primary fs-22 fw-semibold">
                      {initials(profile?.firmaName)}
                    </span>
                  </div>
                </Col>

                <Col>
                  <h4 className="mb-1">{profile?.firmaName || tx("firmaDetay.firma", { firmaId })}</h4>
                  <p className="text-muted mb-2 fs-13">
                    {profile?.firmaShortname ? `${profile.firmaShortname} · ` : ""}
                    {tx("firmaDetay.firmaFuar", { firmaId, fuarId })}
                    {profile?.firmaNation === 2 && <span className="ms-2"><i className="bx bx-world me-1" />{tx("firmaDetay.yabanci")}</span>}
                  </p>

                  <div className="hstack gap-1 flex-wrap">
                    {detay?.isMappedToFuar ? (
                      detay.roles.map((r) => (
                        <span
                          key={r.id}
                          className={`badge bg-${ROLE_COLOR_BY_ID[r.id] || "secondary"}-subtle text-${ROLE_COLOR_BY_ID[r.id] || "secondary"} fw-medium`}
                        >
                          {r.name}
                        </span>
                      ))
                    ) : (
                      <Badge color="light" className="text-muted fw-normal">
                        <i className="bx bx-unlink me-1" />{tx("firmaDetay.buFuaraEslenmemis")}
                      </Badge>
                    )}
                  </div>
                </Col>

                <Col xs={12} xl="auto">
                  <div className="d-flex align-items-center flex-wrap gap-4">
                    <StatBox icon="bx-store"  value={detay?.locations?.length ?? 0}      label={tx("firmaDetay.stand")} />
                    <StatBox icon="bx-package" value={detay?.projects?.length ?? 0}      label={tx("firmaDetay.proje")} />
                    <StatBox icon="bx-user"   value={detay?.users?.length ?? 0}          label={tx("firmaDetay.kullanici")} />
                    <StatBox icon="bx-chat"   value={detay?.talks?.length ?? 0}          label={tx("firmaDetay.gorusme")} />
                    {/* #411: the company's portal at the SELECTED fair, read-only (banner, nothing is saved). The page needs page.firma already. */}
                    <Button
                      color="primary" outline size="sm" data-testid="portal-onizle"
                      title={tx("firmaDetay.portaliOnizleIpucu")}
                      onClick={() => openOnizlemeYeniSekme({ firmaId, firmaAdi: profile?.firmaName })}
                    >
                      <i className="bx bx-show me-1" />{tx("firmaDetay.portaliOnizle")}
                    </Button>
                    <Button color="light" size="sm" onClick={() => navigate("/Management/Firmalar/Kartlar")}>
                      <i className="bx bx-arrow-back me-1" />{tx("firmaDetay.kartlaraDon")}
                    </Button>
                  </div>
                </Col>
              </Row>
            )}
          </CardBody>

          {/* Sekme çubuğu — Velzon'un "profile nav-tabs-custom" deseni */}
          <Nav tabs className="nav-tabs-custom border-bottom-0 px-3">
            {TABS.map((t) => (
              <NavItem key={t.key}>
                <NavLink
                  href="#"
                  className={`fw-semibold ${activeTab === t.key ? "active" : ""}`}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => { e.preventDefault(); setTab(t.key); }}
                >
                  <i className={`bx ${t.icon} me-1 align-middle`} />
                  {t.label}
                  {t.key === "portal-kullanicilar" && bekleyenTalep > 0 && (
                    <span
                      className="badge rounded-pill text-white ms-2" style={{ backgroundColor: "#f06548" }}
                      title={tx("yonetim:kullaniciTalepleri.bekleyenRozetIpucu", { count: bekleyenTalep })} data-testid="portal-kullanici-talep-rozeti"
                    >
                      {bekleyenTalep}
                    </span>
                  )}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </Card>

        {/* ─── Sekme içerikleri ────────────────────────────────────────────── */}
        {/* Her sekme MONTE EDİLDİĞİNDE kendi verisini çeker; TabPane'ler ancak aktif
            olduklarında render ediliyor ki üç ağır ekran aynı anda backend'e gitmesin. */}
        <TabContent activeTab={activeTab} className="mt-3">
          <TabPane tabId="profil">
            {activeTab === "profil" && (
              <FirmaProfilIncelemePage embedded firmaId={firmaId} fuarId={fuarId} />
            )}
          </TabPane>
          <TabPane tabId="personel">
            {activeTab === "personel" && (
              <PersonelIncelemePage embedded firmaId={firmaId} fuarId={fuarId} firmaName={profile?.firmaName} />
            )}
          </TabPane>
          <TabPane tabId="taseron">
            {activeTab === "taseron" && (
              <TaseronBaglantilariPage embedded firmaId={firmaId} fuarId={fuarId} />
            )}
          </TabPane>
          <TabPane tabId="fuar-katalog">
            {activeTab === "fuar-katalog" && (
              <FuarKatalogBilgileriCard firmaId={firmaId} fuarId={fuarId} canEdit />
            )}
          </TabPane>
          <TabPane tabId="kurulum-saati">
            {activeTab === "kurulum-saati" && (
              <FirmaKurulumSaatiSekmesi firmaId={firmaId} fuarId={fuarId} />
            )}
          </TabPane>
          <TabPane tabId="portal-kullanicilar">
            {activeTab === "portal-kullanicilar" && (
              <FirmaPortalKullanicilariSekmesi firmaId={firmaId} fuarId={fuarId} firmaName={profile?.firmaName} bekleyenTalep={bekleyenTalep} />
            )}
          </TabPane>
          <TabPane tabId="portal-kisitlar">
            {activeTab === "portal-kisitlar" && (
              <FirmaPortalKisitlariSekmesi firmaId={firmaId} fuarId={fuarId} />
            )}
          </TabPane>
        </TabContent>
      </Container>
    </div>
  );
}
